import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RGB, Icon, ICONS, CopyButton, ConfirmButton, errMsg, timeAgo } from '../ext/extUi';
import LanTabs from './LanTabs';
import AgentCommands, { isNewer, isWindowsMachine, OsSwitch } from './AgentCommands';

// status → lan.css .lan-tag tone (status is shown as a coloured word)
const RCON_STATE = { ok: 'ok', needs_password: 'warn', fail: 'bad' };

const tunnelUp = (m) => !!(m.tunnel && m.tunnel.state === 'up');

/**
 * Inline "add server" form: from a detected server (port fixed) or manual (port + password).
 * Connection: agent (RCON + logs over the agent) or tunnel (direct, like an online server —
 * IP filled automatically with the machine's tunnel IP, SFTP/FTP optional).
 */
function AddServerForm({ machine, lanServer, onDone, onCancel }) {
  const { t } = useTranslation();
  const manual = !lanServer;
  const canTunnel = tunnelUp(machine);
  const [mode, setMode] = useState(canTunnel ? 'tunnel' : 'agent');
  const tunnel = mode === 'tunnel';
  const passwordKnown = lanServer && lanServer.rcon_state === 'ok';
  const needsPassword = manual || !passwordKnown;
  const [port, setPort] = useState(manual ? '27015' : String(lanServer.port));
  const [password, setPassword] = useState('');
  const [name, setName] = useState(lanServer && lanServer.hostname ? lanServer.hostname : `${machine.name} · ${manual ? 27015 : lanServer.port}`);
  const [lanIp, setLanIp] = useState(machine.lan_ip || '');
  const [sftp, setSftp] = useState({ backup_protocol: 'sftp', backup_port: '', backup_user: '', backup_password: '', backup_remote_path: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    const body = { mode, name: name.trim() || undefined, lan_ip: lanIp.trim() || undefined };
    if (password) body.rcon_password = password;
    if (tunnel && sftp.backup_user.trim()) Object.assign(body, { ...sftp, backup_user: sftp.backup_user.trim(), backup_port: sftp.backup_port ? Number(sftp.backup_port) : undefined });
    try {
      if (manual) await axios.post(`/api/lan/agents/${machine.id}/servers`, { ...body, port: Number(port) });
      else await axios.post(`/api/lan/lan-servers/${lanServer.id}/add`, body);
      onDone();
    } catch (err) { setError(errMsg(err, t('lan.addFailed', 'Could not add the server'))); }
    finally { setBusy(false); }
  };
  const field = (k, lbl, extra = {}) => (
    <div className="lan-field"><label>{lbl}</label><input className="lan-input" value={sftp[k]} onChange={e => setSftp({ ...sftp, [k]: e.target.value })} {...extra} /></div>
  );
  const choice = (value, title, hint, disabled) => (
    <label className={`lan-choice${mode === value ? ' on' : ''}${disabled ? ' disabled' : ''}`}>
      <input type="radio" name={`mode-${machine.id}-${lanServer ? lanServer.id : 'm'}`} checked={mode === value} disabled={disabled} onChange={() => setMode(value)} />
      <span><b>{title}</b><small>{hint}</small></span>
    </label>
  );
  return (
    <form onSubmit={submit} className="lan-form">
      <div className="lan-full lan-choices">
        {choice('tunnel', t('lan.modeTunnel', 'Full access (tunnel)'),
          canTunnel ? t('lan.modeTunnelHint', 'Like an online server: RCON, SFTP/FTP and every port, at {{ip}}', { ip: machine.tunnel.ip })
            : t('lan.modeTunnelUnavailable', 'Needs a connected tunnel on this machine'), !canTunnel)}
        {choice('agent', t('lan.modeAgent', 'RCON only (agent)'), t('lan.modeAgentHint2', 'RCON, logs, forced player names and team side files through the agent. No avatars, demos or file manager.'), !machine.online)}
      </div>
      {manual && (
        <div className="lan-field"><label>{t('lan.port', 'Port')}</label><input className="lan-input" type="number" min="1" max="65535" required value={port} onChange={e => setPort(e.target.value)} /></div>
      )}
      {tunnel && <div className="lan-field"><label>{t('lan.ipAuto', 'IP (automatic)')}</label><input className="lan-input mono" value={machine.tunnel.ip} readOnly /></div>}
      {(needsPassword || tunnel) && (
        <div className="lan-field"><label>{t('lan.rconPassword', 'RCON password')}</label>
          <input className="lan-input" type="password" required={manual} value={password} onChange={e => setPassword(e.target.value)} autoComplete="new-password" placeholder={!manual && passwordKnown ? t('lan.passwordDetected', 'Detected - leave empty') : ''} /></div>
      )}
      <div className="lan-field"><label>{t('lan.serverName', 'Server name')}</label><input className="lan-input" value={name} onChange={e => setName(e.target.value)} maxLength={100} placeholder={t('lan.serverNamePlaceholder', 'e.g. Stage 1')} /></div>
      <div className="lan-field"><label>{t('lan.connectIp', 'LAN IP (players connect to)')}</label><input className="lan-input mono" value={lanIp} onChange={e => setLanIp(e.target.value)} maxLength={64} placeholder="192.168.1.20" /></div>
      {tunnel && (
        <>
          <div className="lan-full lan-label" style={{ marginTop: '0.3rem' }}>
            {t('lan.sftpOptional', 'SFTP / FTP (optional - for player names, avatars, enforce teams, demos)')}
          </div>
          <div className="lan-field"><label>{t('lan.protocol', 'Protocol')}</label>
            <select className="lan-input" value={sftp.backup_protocol} onChange={e => setSftp({ ...sftp, backup_protocol: e.target.value })}><option value="sftp">SFTP</option><option value="ftp">FTP</option></select></div>
          {field('backup_port', t('lan.sftpPort', 'Port'), { type: 'number', placeholder: sftp.backup_protocol === 'ftp' ? '21' : '22' })}
          {field('backup_user', t('lan.sftpUser', 'Username'), { autoComplete: 'off' })}
          {field('backup_password', t('lan.sftpPassword', 'Password'), { type: 'password', autoComplete: 'new-password' })}
          {field('backup_remote_path', t('lan.sftpPath', 'Server path (game/csgo)'), { placeholder: '/home/cs2/serverfiles/game/csgo' })}
        </>
      )}
      <div className="lan-row" style={{ gap: '0.5rem' }}>
        <button type="submit" className="lan-btn primary" disabled={busy}>{busy ? t('common.loading', 'Loading...') : t('lan.addServer', 'Add server')}</button>
        <button type="button" className="lan-btn" onClick={onCancel}>{t('common.cancel', 'Cancel')}</button>
      </div>
      {error && <div className="lan-full lan-error">{error}</div>}
      <div className="lan-full lan-muted">
        {tunnel ? t('lan.passwordNoteTunnel', 'Full access: the RCON password is stored encrypted in the panel, like any online server.')
          : t('lan.passwordNote', 'The password is stored only on the venue machine, never in the panel.')}
      </div>
    </form>
  );
}

// Example only (Steam's default CS2 folder). Servers installed with SteamCMD / WindowsGSM can be on any drive.
const WIN_EXAMPLE_CSGO = 'C:\\Program Files (x86)\\Steam\\steamapps\\common\\Counter-Strike Global Offensive\\game\\csgo';

/** RCON-only server: csgo folder the agent writes the forced-names / side files into (detected, or the admin's). */
function CsgoPathLine({ machine, s, onChanged, setError }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(s.csgo_path || s.csgo_path_detected || '');
  const [busy, setBusy] = useState(false);
  const current = s.csgo_path || s.csgo_path_detected;
  const save = async (path) => {
    setBusy(true);
    try { await axios.put(`/api/lan/lan-servers/${s.id}/csgo-path`, { csgo_path: path }); setEditing(false); onChanged(); }
    catch (e) { setError(errMsg(e, t('lan.actionFailed', 'Action failed'))); }
    finally { setBusy(false); }
  };
  return (
    <div className="lan-path">
      <span title={t('lan.csgoPathHint', 'The agent writes the forced player names and team side files here (instead of SFTP/FTP).')}>{t('lan.csgoPath', 'Match files folder')}:</span>
      {editing ? (
        <form onSubmit={(e) => { e.preventDefault(); save(value); }}>
          <input className="lan-input mono" style={{ flex: 1 }} value={value} onChange={e => setValue(e.target.value)} placeholder={isWindowsMachine(machine) ? WIN_EXAMPLE_CSGO : '/home/cs2/serverfiles/game/csgo'} aria-label={t('lan.csgoPath', 'Match files folder')} autoFocus />
          <button type="submit" className="lan-btn small primary" disabled={busy}>{t('common.save', 'Save')}</button>
          <button type="button" className="lan-btn small" onClick={() => setEditing(false)}>{t('common.cancel', 'Cancel')}</button>
        </form>
      ) : (<>
        {current
          ? <span className="lan-mono">{current}</span>
          : <span className="lan-warn">{t('lan.csgoPathUnknown', 'Unknown - set it, or start the server so the agent can detect it')}</span>}
        {current && <span className={`lan-tag${s.csgo_path ? ' info' : ''}`}>{s.csgo_path ? t('lan.csgoPathCustom', 'set by admin') : t('lan.csgoPathAuto', 'detected')}</span>}
        <button type="button" className="lan-btn small" onClick={() => { setValue(current || ''); setEditing(true); }}>{t('lan.change', 'Change')}</button>
        {s.csgo_path && <button type="button" className="lan-btn small" disabled={busy} onClick={() => save('')}>{t('lan.csgoPathReset', 'Use detected')}</button>}
      </>)}
    </div>
  );
}

function ServerRow({ machine, s, onChanged, setError }) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [switching, setSwitching] = useState(false);
  const remove = async () => {
    try { await axios.delete(`/api/lan/lan-servers/${s.id}`); onChanged(); } catch (e) { setError(errMsg(e, t('lan.actionFailed', 'Action failed'))); }
  };
  const switchMode = async (mode) => {
    setSwitching(true);
    try { await axios.post(`/api/lan/lan-servers/${s.id}/mode`, { mode }); onChanged(); } catch (e) { setError(errMsg(e, t('lan.actionFailed', 'Action failed'))); }
    finally { setSwitching(false); }
  };
  const isTunnel = s.mode === 'tunnel';
  return (
    <div className="lan-srv">
      <div className="lan-srv-line">
        <span className="lan-port">:{s.port}</span>
        <span className="lan-srv-name">{s.hostname || t('lan.unknownHostname', 'Unknown server')}</span>
        {s.map && <span className="lan-muted">{s.map}</span>}
        {s.max_players != null && <span className="lan-muted">{t('lan.playersN', '{{n}}/{{max}} players', { n: s.players || 0, max: s.max_players })}</span>}
        {s.rcon_state && <span className={`lan-tag ${RCON_STATE[s.rcon_state] || ''}`}>{t(`lan.rcon.${s.rcon_state}`, s.rcon_state)}</span>}
        {!s.detected && <span className="lan-tag">{t('lan.manual', 'Manual')}</span>}
        <span className="lan-spacer" />
        {s.server ? (
          <>
            <span className={`lan-tag${isTunnel ? ' full' : ''}`}>{isTunnel ? t('lan.modeTunnelShort', 'Full access') : t('lan.modeAgentShort', 'RCON only')}</span>
            {!s.server.current_match_id && (isTunnel
              ? <button type="button" className="lan-btn small" disabled={switching || !machine.online} onClick={() => switchMode('agent')}>{t('lan.switchToAgent', 'Switch to RCON only')}</button>
              : tunnelUp(machine) && <button type="button" className="lan-btn small full" disabled={switching} onClick={() => switchMode('tunnel')}>{t('lan.switchToTunnel', 'Switch to full access')}</button>)}
            {isTunnel && <Link to={`/admin/servers/${s.server.id}/files`} className="lan-btn small full">{t('lan.files', 'Files')}</Link>}
            <Link to="/admin/servers" className="lan-tag info">
              <Icon d={ICONS.check} size={12} /> {t('lan.addedAs', 'Added as {{name}}', { name: s.server.name })}
            </Link>
          </>
        ) : (
          <>
            {!adding && <button type="button" className="lan-btn small good" disabled={machine.status !== 'approved' || !(machine.online || tunnelUp(machine))} onClick={() => setAdding(true)}><Icon d={ICONS.plus} /> {t('lan.add', 'Add')}</button>}
            {!s.detected && <ConfirmButton label={t('lan.remove', 'Remove')} warning={t('lan.removeServerWarn', 'Remove this server from the machine?')} icon={ICONS.trash} onConfirm={remove} />}
          </>
        )}
      </div>
      {s.server && !isTunnel && <CsgoPathLine key={`${s.csgo_path}|${s.csgo_path_detected}`} machine={machine} s={s} onChanged={onChanged} setError={setError} />}
      {adding && <AddServerForm machine={machine} lanServer={s} onCancel={() => setAdding(false)} onDone={() => { setAdding(false); onChanged(); }} />}
    </div>
  );
}

/** Tunnel status line of a machine (Full-access installs) + on/off switch. */
function TunnelStatus({ m, act }) {
  const { t } = useTranslation();
  const tn = m.tunnel || {};
  if (tn.install_mode !== 'full') {
    return (
      <div className="lan-tunnel">
        {isWindowsMachine(m) ? (<>
          <span className="lan-tag">{t('lan.rconOnlyWindows', 'RCON only (Windows)')}</span>
          <span className="lan-muted">{t('lan.rconOnlyHintWin', 'Full access (tunnel, SFTP/FTP) is not available on Windows yet.')}</span>
        </>) : (<>
          <span className="lan-tag">{t('lan.rconOnlyInstall', 'RCON only (chosen at install)')}</span>
          <span className="lan-muted">{t('lan.rconOnlyHint', 'For full access, re-run the install command on this machine and choose option 2.')}</span>
        </>)}
      </div>
    );
  }
  const tone = { up: 'ok', waiting: 'warn', pending: 'warn', off: '', hub_unavailable: 'bad' }[tn.state] || '';
  const text = {
    up: t('lan.tunnelUp', 'Tunnel connected'),
    waiting: t('lan.tunnelWaiting', 'Waiting for handshake - check that outbound UDP {{port}} is allowed at the venue', { port: 51820 }),
    pending: t('lan.tunnelPending', 'Setting up…'),
    off: t('lan.tunnelOff', 'Tunnel switched off'),
    hub_unavailable: tn.error || t('lan.tunnelHubDown', 'Tunnel unavailable on this panel'),
  }[tn.state] || tn.state;
  return (
    <div className="lan-tunnel">
      <span className="lan-tag full">{t('lan.fullAccess', 'Full access')}</span>
      <span className={tone ? `lan-tag ${tone}` : undefined}>{text}</span>
      {tn.ip && tn.state !== 'off' && <><span className="lan-mono">{tn.ip}</span><CopyButton text={tn.ip} small /></>}
      {tn.state === 'up' && tn.last_handshake_at && <span className="lan-muted">{t('lan.handshakeAgo', 'handshake {{when}} ago', { when: timeAgo(tn.last_handshake_at) })}</span>}
      {tn.machine && tn.machine.error && <span className="lan-error">{t('lan.machineSays', 'Machine: {{error}}', { error: tn.machine.error })}</span>}
      <span className="lan-spacer" />
      {m.status === 'approved' && (tn.enabled
        ? <ConfirmButton label={t('lan.tunnelDisable', 'Turn off')} color={RGB.amber} warning={t('lan.tunnelDisableWarn', 'Full-access servers on this machine become unreachable until it is turned on again.')} onConfirm={() => act(() => axios.post(`/api/lan/agents/${m.id}/tunnel`, { enabled: false }))} />
        : <button type="button" className="lan-btn small full" onClick={() => act(() => axios.post(`/api/lan/agents/${m.id}/tunnel`, { enabled: true }))}>{t('lan.tunnelEnable', 'Turn on')}</button>)}
    </div>
  );
}

function MachineCard({ m, latest, onChanged, setError }) {
  const { t } = useTranslation();
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(m.name || '');
  const [manualAdd, setManualAdd] = useState(false);
  const act = async (fn) => { try { await fn(); onChanged(); } catch (e) { setError(errMsg(e, t('lan.actionFailed', 'Action failed'))); } };
  const statusText = m.status === 'approved' ? (m.online ? t('lan.online', 'Online') : t('lan.offline', 'Offline')) : t(`lan.status.${m.status}`, m.status);
  const statusTone = m.status === 'pending' ? 'warn' : m.status !== 'approved' ? 'bad' : m.online ? 'ok' : '';
  return (
    <div className="lan-machine">
      <div className="lan-machine-head">
        {renaming ? (
          <form onSubmit={(e) => { e.preventDefault(); act(async () => { await axios.patch(`/api/lan/agents/${m.id}`, { name: newName }); setRenaming(false); }); }} className="lan-row" style={{ gap: '0.4rem' }}>
            <input className="lan-input" value={newName} onChange={e => setNewName(e.target.value)} maxLength={100} style={{ width: 220 }} aria-label={t('lan.rename', 'Rename')} autoFocus />
            <button type="submit" className="lan-btn small primary">{t('common.save', 'Save')}</button>
            <button type="button" className="lan-btn small" onClick={() => setRenaming(false)}>{t('common.cancel', 'Cancel')}</button>
          </form>
        ) : (
          <span className="lan-machine-name">{m.name || m.hostname || m.uid}</span>
        )}
        {!renaming && <button type="button" className="lan-icon-btn" title={t('lan.rename', 'Rename')} aria-label={t('lan.rename', 'Rename')} onClick={() => { setNewName(m.name || ''); setRenaming(true); }}><Icon d={ICONS.edit} /></button>}
        <span className={`lan-tag ${statusTone}`}>{statusText}</span>
        <span className="lan-spacer" />
        {m.status === 'pending' && (
          <>
            <button type="button" className="lan-btn small primary" onClick={() => act(() => axios.post(`/api/lan/agents/${m.id}/approve`))}><Icon d={ICONS.check} /> {t('lan.approve', 'Approve')}</button>
            <ConfirmButton label={t('lan.reject', 'Reject')} warning={t('lan.rejectWarn', 'Reject this machine?')} onConfirm={() => act(() => axios.post(`/api/lan/agents/${m.id}/reject`))} />
          </>
        )}
        {m.status === 'approved' && (
          <>
            <button type="button" className="lan-btn small" disabled={!m.online} onClick={() => act(() => axios.post(`/api/lan/agents/${m.id}/rescan`))}><Icon d={ICONS.refresh} /> {t('lan.rescan', 'Rescan')}</button>
            <ConfirmButton label={t('lan.revoke', 'Revoke')} warning={t('lan.revokeWarn', 'Disconnects this machine; its servers stop working until it is re-installed.')} onConfirm={() => act(() => axios.post(`/api/lan/agents/${m.id}/revoke`))} />
          </>
        )}
        {(m.status === 'revoked' || m.status === 'rejected' || m.status === 'uninstalled') && (
          <ConfirmButton label={t('lan.remove', 'Remove')} warning={t('lan.removeMachineWarn', 'Remove this machine from the event?')} icon={ICONS.trash} onConfirm={() => act(() => axios.delete(`/api/lan/agents/${m.id}`))} />
        )}
      </div>
      <div className="lan-meta">
        {m.hostname && <span>{t('lan.hostname', 'Host')}: <span className="lan-mono">{m.hostname}</span></span>}
        {m.lan_ip && <span>{t('lan.lanIp', 'LAN IP')}: <span className="lan-mono">{m.lan_ip}</span></span>}
        {m.os && <span>{m.os}</span>}
        {m.version && <span>{t('lan.agentVersion', 'Agent')} {m.version}</span>}
        {m.version && latest && isNewer(latest, m.version) && m.status !== 'uninstalled' && (
          <span title={isWindowsMachine(m) ? t('lan.updateHintWin', 'Run on the machine in PowerShell as Administrator: flux-agent update') : t('lan.updateHint', 'Run on the machine: sudo flux-agent update')}>
            <span className="lan-tag warn">{t('lan.updateAvailable', 'Update available ({{version}})', { version: latest })}</span>{' '}
            <span className="lan-mono">{isWindowsMachine(m) ? 'flux-agent update' : 'sudo flux-agent update'}</span>
          </span>
        )}
        <span>{m.online ? t('lan.connected', 'Connected') : t('lan.lastSeen', 'Last seen {{when}}', { when: timeAgo(m.last_seen_at) })}</span>
      </div>
      <TunnelStatus m={m} act={act} />
      {m.status === 'pending' && (
        <p className="lan-note">
          {t('lan.pendingHint', 'Check that this is one of the venue machines, then approve it. Until then it cannot receive commands.')}
        </p>
      )}
      {m.status === 'approved' && (
        <div className="lan-servers">
          <div className="lan-servers-head">
            <span className="lan-label">{t('lan.cs2Servers', 'CS2 servers')}</span>
            <span className="lan-spacer" />
            {!manualAdd && <button type="button" className="lan-btn small" disabled={!m.online && !tunnelUp(m)} onClick={() => setManualAdd(true)}><Icon d={ICONS.plus} /> {t('lan.addManually', 'Add manually')}</button>}
          </div>
          {manualAdd && <AddServerForm machine={m} lanServer={null} onCancel={() => setManualAdd(false)} onDone={() => { setManualAdd(false); onChanged(); }} />}
          {m.servers.length === 0 && !manualAdd && (
            <div className="lan-muted" style={{ padding: '0.4rem 0.25rem' }}>
              {m.online ? t('lan.noServersFound', 'No running CS2 servers found on this machine yet. Start them and press Rescan, or add one manually.') : t('lan.machineOffline', 'This machine is offline.')}
            </div>
          )}
          {m.servers.map(s => <ServerRow key={s.id} machine={m} s={s} onChanged={onChanged} setError={setError} />)}
        </div>
      )}
    </div>
  );
}

export default function LanEventDetail() {
  const { id } = useParams();
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const [ev, setEv] = useState(null);
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [installOs, setInstallOs] = useState('linux');
  const wsRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`/api/lan/events/${id}`);
      setEv(r.data);
      setNotFound(false);
    } catch (e) {
      if (e.response && (e.response.status === 404 || e.response.status === 403)) setNotFound(true);
      else setError(errMsg(e, t('lan.loadFailed', 'Failed to load')));
    }
  }, [id, t]);

  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, [load]);

  // Instant refresh when a machine connects / reports servers (admin WS `lan_update`).
  useEffect(() => {
    if (!token) return undefined;
    let closed = false;
    const ws = new WebSocket(`${window.location.origin.replace(/^http/, 'ws')}/ws?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onmessage = (msg) => {
      try { const m = JSON.parse(msg.data); if (m.type === 'lan_update' && String(m.event_id) === String(id) && !closed) load(); } catch (_) { /* ignore */ }
    };
    return () => { closed = true; try { ws.close(); } catch (_) { /* ignore */ } };
  }, [token, id, load]);

  const patch = async (body) => { try { const r = await axios.patch(`/api/lan/events/${id}`, body); setEv(prev => ({ ...prev, ...r.data })); } catch (e) { setError(errMsg(e, t('lan.actionFailed', 'Action failed'))); } };
  const post = async (path, body) => { try { await axios.post(`/api/lan/events/${id}/${path}`, body || {}); await load(); } catch (e) { setError(errMsg(e, t('lan.actionFailed', 'Action failed'))); } };

  if (notFound) return <div className="lan-page"><LanTabs /><p className="lan-lead">{t('lan.notFound', 'LAN event not found.')} <Link to="/admin/servers/lan">{t('lan.back', 'Back')}</Link></p></div>;
  if (!ev) return <div className="lan-page"><LanTabs /><div className="lan-muted">{t('common.loading', 'Loading...')}</div></div>;

  const active = ev.status === 'active';
  const machines = ev.machines || [];
  return (
    <div className="lan-page">
      <LanTabs />
      <div className="lan-head">
        <Link to="/admin/servers/lan" className="lan-back">
          <Icon d={ICONS.back} size={13} /> {t('lan.title', 'LAN Environments')}
        </Link>
        <div className="lan-title-row">
          <h1 className="lan-title">{ev.name}</h1>
          <span className={`lan-tag${active ? ' ok' : ''}`}>{active ? t('lan.active', 'Active') : t('lan.ended', 'Ended')}</span>
          <span className="lan-meta">
            <span>{t('lan.machinesOnline', '{{online}}/{{total}} machines online', { online: ev.agents_online, total: ev.agents })}</span>
            <span>{t('lan.serversN', '{{n}} servers', { n: ev.servers })}</span>
          </span>
          <span className="lan-spacer" />
          {active
            ? <ConfirmButton label={t('lan.endEvent', 'End event')} warning={t('lan.endEventWarn', 'Disconnects every machine of this event and disables its servers.')} onConfirm={() => post('end')} />
            : <button type="button" className="lan-btn" onClick={() => post('reopen')}>{t('lan.reopen', 'Reopen')}</button>}
        </div>
      </div>

      {error && <div className="lan-errbar" role="alert">
        <span className="lan-spacer">{error}</span><button type="button" className="lan-btn small" onClick={() => setError('')}>{t('common.close', 'Close')}</button>
      </div>}

      {active && (
        <section className="lan-block">
          <div className="lan-block-head">
            <h2 className="lan-h2">{t('lan.installTitle', 'Install command')}</h2>
            <OsSwitch value={installOs} onChange={setInstallOs} />
          </div>
          {installOs === 'windows' ? (
            <p className="lan-sub">{t('lan.installHintWin', 'Paste this into PowerShell opened as Administrator on every server machine of the venue (Windows 10/11 or Server, 64-bit). Start the CS2 servers first so the agent can detect them. Windows machines use RCON only (commands and logs through the agent); Full access is Linux only for now. Nothing needs to be port-forwarded.')}</p>
          ) : (<>
            <p className="lan-sub">{t('lan.installHint', 'Run this as root on every server machine of the venue (Ubuntu/Debian). Start the CS2 servers first so the agent can detect them. Nothing needs to be port-forwarded.')}</p>
            <p className="lan-sub">{t('lan.installModeHint', 'The installer asks: 1) RCON only, or 2) Full access - the panel reaches that machine like an online server (SFTP/FTP, all ports) through a WireGuard tunnel. Add --mode rcon or --mode full to skip the question.')}</p>
          </>)}
          {ev.install_command && !ev.token_expired ? (
            <div className="lan-code-row">
              <code className="lan-code">{installOs === 'windows' ? ev.install_command_windows : ev.install_command}</code>
              <CopyButton text={installOs === 'windows' ? ev.install_command_windows : ev.install_command} />
            </div>
          ) : (
            <div className="lan-warn">{t('lan.tokenExpired', 'This install command has expired. Regenerate it to add more machines.')}</div>
          )}
          <div className="lan-row" style={{ marginTop: '0.75rem' }}>
            <span className="lan-muted">
              {ev.token_expired ? t('lan.expired', 'Expired') : t('lan.expires', 'Valid until {{date}}', { date: new Date(ev.token_expires_at).toLocaleString() })}
            </span>
            <label className="lan-check">
              <input type="checkbox" checked={!!ev.auto_approve} onChange={e => patch({ auto_approve: e.target.checked })} />
              {t('lan.autoApprove', 'Approve new machines automatically')}
            </label>
            <span className="lan-spacer" />
            <ConfirmButton label={t('lan.regenerate', 'Regenerate')} color={RGB.amber} icon={ICONS.refresh} warning={t('lan.regenerateWarn', 'The old command stops working. Machines already connected are not affected.')} onConfirm={() => post('regenerate-token')} />
          </div>
        </section>
      )}

      <section className="lan-block" style={{ paddingBottom: 0 }}>
        <h2 className="lan-h2">{t('lan.machines', 'Machines')}</h2>
        {machines.length === 0 && (
          <p className="lan-muted" style={{ margin: '0.6rem 0 0' }}>{t('lan.noMachines', 'No machines yet. Run the install command on a venue server machine - it shows up here within a few seconds.')}</p>
        )}
      </section>
      {machines.map(m => <MachineCard key={m.id} m={m} latest={ev.latest_agent_version} onChanged={load} setError={setError} />)}

      <AgentCommands installCommand={active && !ev.token_expired ? ev.install_command : null} installCommandWindows={active && !ev.token_expired ? ev.install_command_windows : null} machines={machines} latest={ev.latest_agent_version} />
    </div>
  );
}
